import React, { useRef, useEffect, useCallback } from 'react';
import ProductSearch from './ProductSearch';

const CartTable = ({
  cart,
  appliedDiscounts,
  priceLimitWarnings,
  editingPrices,
  setEditingPrices,
  productSearchQuery,
  setProductSearchQuery,
  selectedCategory,
  onlyAvailable,
  currentLocationId,
  updateQuantity,
  updatePrice,
  removeFromCart,
  toggleDiscountExclusion,
  handleProductSelect,
  getEffectiveDiscount,
  premiumProducts = []
}) => {
  // Set of premium product IDs for quick lookup
  const premiumProductIds = React.useMemo(() => {
    const ids = new Set();
    const katIds = new Set();
    premiumProducts.forEach(p => {
      if (p.product_id) ids.add(p.product_id);
      if (p.kategoria_id) katIds.add(p.kategoria_id);
    });
    return { ids, katIds };
  }, [premiumProducts]);

  // --- Animacja flash + bounce przy zmianie ilości ---
  const prevQuantitiesRef = useRef({});
  const [flashItems, setFlashItems] = React.useState({});
  const flashTimeoutsRef = useRef({});
  const tableRef = useRef(null);

  useEffect(() => {
    const prev = prevQuantitiesRef.current;
    const newFlash = {};
    cart.forEach(item => {
      const prevQty = prev[item.id];
      if (prevQty !== undefined && item.quantity > prevQty) {
        newFlash[item.id] = true;
        // Auto-scroll do wiersza
        const row = tableRef.current?.querySelector(`[data-item-id="${item.id}"]`);
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
    if (Object.keys(newFlash).length > 0) {
      setFlashItems(f => ({ ...f, ...newFlash }));
      Object.keys(newFlash).forEach(id => {
        if (flashTimeoutsRef.current[id]) clearTimeout(flashTimeoutsRef.current[id]);
        flashTimeoutsRef.current[id] = setTimeout(() => {
          setFlashItems(f => { const n = { ...f }; delete n[id]; return n; });
        }, 900);
      });
    }
    // Update prev quantities
    const next = {};
    cart.forEach(item => { next[item.id] = item.quantity; });
    prevQuantitiesRef.current = next;
  }, [cart]);

  const isPremiumProduct = (item) => {
    return premiumProductIds.ids.has(item.id) || premiumProductIds.katIds.has(item.kategoria_id);
  };

  const getPremiumBonus = (item) => {
    const bp = premiumProducts.find(p => p.product_id === item.id || (p.kategoria_id && p.kategoria_id === item.kategoria_id));
    return bp ? bp.kwota_za_sztuke : 0;
  };
  return (
    <div style={{ padding: "1rem" }}>
      {/* CSS animacje dla flash wiersza i bounce ilości */}
      <style>{`
        @keyframes cart-row-flash {
          0% { background-color: rgba(34, 197, 94, 0.3); }
          100% { background-color: transparent; }
        }
        @keyframes cart-qty-bounce {
          0% { transform: scale(1); }
          30% { transform: scale(1.6); }
          60% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
        .cart-row-flash {
          animation: cart-row-flash 0.9s ease-out forwards;
        }
        .cart-qty-bounce {
          animation: cart-qty-bounce 0.4s ease-out;
          display: inline-block;
          color: var(--success) !important;
          font-weight: 700 !important;
        }
      `}</style>
      <div
        ref={tableRef}
        style={{
          maxHeight: "500px",
          overflowY: "auto",
          marginBottom: "20px",
          border: "1px solid var(--border)",
          borderRadius: "4px",
        }}
      >
        <table style={{ width: "100%", fontSize: "12px" }}>
          <thead
            style={{
              backgroundColor: "var(--bg)",
              position: "sticky",
              top: 0,
            }}
          >
            <tr>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "center",
                  width: "40px",
                }}
              >
                LP
              </th>
              <th style={{ padding: "8px 6px", textAlign: "left" }}>
                Produkt
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "center",
                  width: "80px",
                }}
              >
                Ilość
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "center",
                  width: "60px",
                }}
              >
                VAT
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "right",
                  width: "90px",
                }}
              >
                Cena netto
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "right",
                  width: "90px",
                }}
              >
                Cena brutto
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "center",
                  width: "70px",
                }}
              >
                Rabat
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "right",
                  width: "90px",
                }}
              >
                Wartość
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "center",
                  width: "140px",
                }}
              >
                Marża
              </th>
              <th
                style={{
                  padding: "8px 6px",
                  textAlign: "center",
                  width: "60px",
                }}
              >
                Akcje
              </th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 && (
              <tr>
                <td
                  colSpan="10"
                  style={{
                    textAlign: "center",
                    padding: "2rem 1rem",
                    color: "var(--text-muted)",
                  }}
                >
                  <i
                    className="fas fa-shopping-cart"
                    style={{
                      fontSize: "2rem",
                      marginBottom: "0.5rem",
                      opacity: 0.3,
                      display: "block",
                    }}
                  ></i>
                  <div
                    style={{ fontSize: "0.9rem", fontWeight: "500" }}
                  >
                    Koszyk jest pusty
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    Użyj wyszukiwarki poniżej, aby dodać produkty
                  </div>
                </td>
              </tr>
            )}
            {(() => {
              // Wspolczynnik rabatu koszykowego - marza na zywo musi byc liczona od ceny
              // FAKTYCZNIE placonej (po rabacie), tak jak backend przy finalizacji
              // (utils/discount.py: rabat rozkladany rownomiernie na pozycje).
              // Bez tego zmiana ceny przeliczala marze, a udzielenie rabatu - nie.
              const cartTotalBrutto = cart.reduce((s, it) => {
                const p = editingPrices && editingPrices[it.id] !== undefined ? editingPrices[it.id] : it.price;
                return s + (parseFloat(p) || 0) * (parseFloat(it.quantity) || 0);
              }, 0);
              const effDiscount = typeof getEffectiveDiscount === 'function' ? (parseFloat(getEffectiveDiscount()) || 0) : 0;
              const discountCoeff = cartTotalBrutto > 0 && effDiscount > 0
                ? Math.max((cartTotalBrutto - effDiscount) / cartTotalBrutto, 0)
                : 1;
              return cart.map((item, index) => {
              // Obliczenia dla ceny netto/brutto, marży, total itp.
              const taxRate = parseFloat(item.stawka_vat) || 23;
              const bruttoPrice = parseFloat(item.price) || 0;
              const nettoPrice = bruttoPrice / (1 + taxRate / 100);
              const total = bruttoPrice * (parseFloat(item.quantity) || 0);
              // Ceny PO rabacie (do marzy); kaucje nie podlegaja rabatowi
              const isDepositItem = parseFloat(item.stawka_vat) === -2 || item.typ_produktu === 'kaucja';
              const itemCoeff = isDepositItem ? 1 : discountCoeff;
              const nettoAfterDiscount = nettoPrice * itemCoeff;
              const bruttoAfterDiscount = bruttoPrice * itemCoeff;

              // Użyj dynamicznie obliczonej marży jeśli dostępna, w przeciwnym razie oblicz z aktualnej ceny zakupu
              let margaAbsolute, margaPercent;
              const currentPurchasePrice = parseFloat(item.current_purchase_price) || 0;
              const cenaZakupu = parseFloat(item.cena_zakupu) || 0;

              if (item.margin_percent !== null && item.margin_amount !== null && itemCoeff === 1) {
                // Użyj dynamicznie obliczonej marży (tylko bez rabatu - marza z API nie zna rabatu)
                margaPercent = parseFloat(item.margin_percent) || 0;
                margaAbsolute = parseFloat(item.margin_amount) || 0;
              } else if (item.margin_percent !== null && item.margin_amount !== null && nettoPrice > 0) {
                // Rabat aktywny: wyprowadz cene zakupu z marzy z API i przelicz od ceny po rabacie
                const derivedPurchase = nettoPrice - (parseFloat(item.margin_amount) || 0);
                margaAbsolute = nettoAfterDiscount - derivedPurchase;
                margaPercent = nettoAfterDiscount > 0 ? (margaAbsolute / nettoAfterDiscount) * 100 : 0;
              } else if (currentPurchasePrice > 0) {
                // Oblicz marżę z najnowszej ceny zakupu (od ceny po rabacie)
                margaAbsolute = nettoAfterDiscount - currentPurchasePrice;
                margaPercent = nettoAfterDiscount > 0 ? (margaAbsolute / nettoAfterDiscount) * 100 : 0;
              } else if (cenaZakupu > 0) {
                // Fallback - stara metoda obliczania (od ceny po rabacie)
                margaAbsolute = bruttoAfterDiscount - cenaZakupu;
                margaPercent = cenaZakupu > 0 ? ((bruttoAfterDiscount - cenaZakupu) / cenaZakupu) * 100 : 0;
              } else {
                // Brak danych do obliczenia marży
                margaAbsolute = 0;
                margaPercent = 0;
              }

              // Marza FIFO (glowna, widoczna kolumna) - te sama korekta o rabat:
              // przelicz % od ceny PO rabacie, inaczej po udzieleniu rabatu stala w miejscu.
              const fifoPurchase = parseFloat(item.fifo_purchase_price) || 0;
              let fifoMargaPercent = parseFloat(item.fifo_margin_percent) || 0;
              if (fifoPurchase > 0 && nettoAfterDiscount > 0) {
                fifoMargaPercent = (nettoAfterDiscount - fifoPurchase) / nettoAfterDiscount * 100;
              }
              const isFlashing = flashItems[item.id];
              return (
                <tr
                  key={item.id}
                  data-item-id={item.id}
                  className={isFlashing ? 'cart-row-flash' : ''}
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "center",
                      fontSize: "11px",
                      fontWeight: "500",
                      color: "var(--text-muted)",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td style={{ padding: "8px 6px" }}>
                    <div
                      style={{
                        fontWeight: "500",
                        fontSize: "11px",
                        color: item.is_deposit ? "var(--info)" : item.wykluczony_z_rabatow ? "var(--danger)" : "inherit"
                      }}
                    >
                      {item.is_deposit && <span style={{ marginRight: "4px" }}>🔄</span>}
                      {item.wykluczony_z_rabatow && !item.is_deposit && <span style={{ marginRight: "4px" }} title="Produkt wykluczony z rabatów">🚫</span>}
                      {item.name}
                      {isPremiumProduct(item) && (
                        <span style={{
                          marginLeft: '4px',
                          fontSize: '9px',
                          color: '#f59e0b',
                          fontWeight: 600
                        }} title={`Premiowany: +${getPremiumBonus(item).toFixed(2)} zł/szt.`}>
                          ⭐ +{getPremiumBonus(item).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {item.is_deposit && (
                      <div style={{ fontSize: "9px", color: "var(--info)", fontStyle: "italic" }}>
                        Kaucja zwrotna
                      </div>
                    )}
                    {item.kod_kreskowy && (
                      <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                        {item.kod_kreskowy}
                      </div>
                    )}
                    {/* Wyświetl dostępny stan magazynowy */}
                    {item.available_stock !== undefined && !item.is_deposit && (
                      <div style={{
                        fontSize: "9px",
                        color: item.available_stock < item.quantity ? "var(--danger)" : "var(--success)",
                        fontWeight: item.available_stock < item.quantity ? "600" : "normal"
                      }}>
                        {item.available_stock < item.quantity ? (
                          <>⚠️ Dostępne: {item.available_stock} szt. (brakuje {item.quantity - item.available_stock})</>
                        ) : (
                          <>📦 Dostępne: {item.available_stock} szt.</>
                        )}
                      </div>
                    )}
                    {/* Ostrzeżenie o granicy ceny */}
                    {priceLimitWarnings[item.id] && (
                      <div style={{
                        fontSize: "9px",
                        color: "var(--danger)",
                        fontWeight: "600",
                        backgroundColor: "var(--danger-light)",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        marginTop: "2px"
                      }}>
                        🚫 {priceLimitWarnings[item.id].message}
                      </div>
                    )}
                  </td>
                  <td
                    style={{ padding: "8px 6px", textAlign: "center" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "3px",
                      }}
                    >
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.quantity - 1)
                        }
                        style={{
                          background: "var(--bg)",
                          width: "32px",
                          height: "32px",
                          minWidth: "32px",
                          minHeight: "32px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0
                        }}
                      >
                        -
                      </button>
                      <span
                        key={isFlashing ? `qty-${item.id}-${item.quantity}` : `qty-${item.id}`}
                        className={isFlashing ? 'cart-qty-bounce' : ''}
                        style={{
                          minWidth: "24px",
                          textAlign: "center",
                          fontSize: isFlashing ? "13px" : "11px",
                          fontWeight: isFlashing ? "700" : "500",
                        }}
                      >
                        {item.quantity}
                      </span>
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.quantity + 1)
                        }
                        style={{
                          background: "var(--bg)",
                          width: "32px",
                          height: "32px",
                          minWidth: "32px",
                          minHeight: "32px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0
                        }}
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "center",
                      backgroundColor: item.stawka_vat === -2 ? 'var(--border)' : 'transparent',
                      fontWeight: item.stawka_vat === -2 ? '600' : 'normal'
                    }}
                  >
                    {item.stawka_vat === -2 ? 'NP' : item.stawka_vat === -1 ? 'ZW' : `${item.stawka_vat || 23}%`}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontSize: "11px",
                      color: "var(--text-muted)",
                    }}
                  >
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={
                        editingPrices[`${item.id}_netto`] !== undefined
                          ? editingPrices[`${item.id}_netto`]
                          : nettoPrice.toFixed(2)
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditingPrices((prev) => ({
                          ...prev,
                          [`${item.id}_netto`]: value,
                        }));
                        if (value !== "" && !isNaN(parseFloat(value))) {
                          updatePrice(
                            item.id,
                            parseFloat(value) || 0,
                            true,
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        // Cena nie moze byc ujemna - minus (takze numeryczny)
                        // nie wchodzi do pola z klawiatury.
                        if (e.key === '-' || e.key === 'Subtract') {
                          e.preventDefault();
                          return;
                        }
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                          const stateKey = `${item.id}_netto`;
                          const currentValue = editingPrices[stateKey] !== undefined
                            ? parseFloat(editingPrices[stateKey]) || 0
                            : nettoPrice;
                          const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(0, currentValue - 1);
                          const formatted = newValue.toFixed(2);
                          setEditingPrices((prev) => ({
                            ...prev,
                            [`${item.id}_netto`]: formatted,
                          }));
                          updatePrice(item.id, newValue, true);
                        }
                      }}
                      onFocus={(e) => {
                        e.target.select();
                        setEditingPrices((prev) => ({
                          ...prev,
                          [`${item.id}_netto`]: nettoPrice.toFixed(2),
                        }));
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        updatePrice(item.id, value, true);
                        setEditingPrices((prev) => {
                          const newState = { ...prev };
                          delete newState[`${item.id}_netto`];
                          return newState;
                        });
                      }}
                      style={{
                        width: "70px",
                        padding: "2px 4px",
                        fontSize: "10px",
                        border: "1px solid var(--border)",
                        borderRadius: "3px",
                        textAlign: "right",
                        backgroundColor: "var(--card-bg)",
                        fontWeight: "500",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={
                        editingPrices[`${item.id}_brutto`] !== undefined
                          ? editingPrices[`${item.id}_brutto`]
                          : bruttoPrice.toFixed(2)
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditingPrices((prev) => ({
                          ...prev,
                          [`${item.id}_brutto`]: value,
                        }));
                        if (value !== "" && !isNaN(parseFloat(value))) {
                          updatePrice(
                            item.id,
                            parseFloat(value) || 0,
                            false,
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        // Cena nie moze byc ujemna - minus (takze numeryczny)
                        // nie wchodzi do pola z klawiatury.
                        if (e.key === '-' || e.key === 'Subtract') {
                          e.preventDefault();
                          return;
                        }
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                          const stateKey = `${item.id}_brutto`;
                          const currentValue = editingPrices[stateKey] !== undefined
                            ? parseFloat(editingPrices[stateKey]) || 0
                            : bruttoPrice;
                          const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(0, currentValue - 1);
                          const formatted = newValue.toFixed(2);
                          setEditingPrices((prev) => ({
                            ...prev,
                            [`${item.id}_brutto`]: formatted,
                          }));
                          updatePrice(item.id, newValue, false);
                        }
                      }}
                      onFocus={(e) => {
                        e.target.select();
                        setEditingPrices((prev) => ({
                          ...prev,
                          [`${item.id}_brutto`]: bruttoPrice.toFixed(2),
                        }));
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        updatePrice(item.id, value, false);
                        setEditingPrices((prev) => {
                          const newState = { ...prev };
                          delete newState[`${item.id}_brutto`];
                          return newState;
                        });
                      }}
                      style={{
                        width: "70px",
                        padding: "2px 4px",
                        fontSize: "10px",
                        border: "1px solid var(--border)",
                        borderRadius: "3px",
                        textAlign: "right",
                        backgroundColor: "var(--card-bg)",
                        fontWeight: "500",
                      }}
                    />
                  </td>
                  {/* Kolumna Rabat */}
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "center",
                      fontSize: "10px",
                    }}
                  >
                    {/* Kaucje (VAT = -2) i produkty wykluczone z rabatów nie mają rabatu */}
                    {appliedDiscounts.length > 0 && taxRate !== -2 && item.typ_produktu !== 'kaucja' && !item.wykluczony_z_rabatow ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                        {appliedDiscounts.map(discount => {
                          // Oblicz rabat dla tej pozycji proporcjonalnie (tylko dla produktów które mogą mieć rabat)
                          const cartTotalForDiscount = cart.reduce((sum, i) => {
                            const vat = parseFloat(i.stawka_vat);
                            if (vat === -2 || i.typ_produktu === 'kaucja') return sum;
                            if (i.wykluczony_z_rabatow) return sum; // Wykluczone z rabatów
                            return sum + (parseFloat(i.price) || 0) * (parseFloat(i.quantity) || 0);
                          }, 0);
                          const itemRatio = cartTotalForDiscount > 0 ? total / cartTotalForDiscount : 0;
                          // Użyj efektywnego rabatu zamiast oryginalnego
                          const effectiveDiscountTotal = getEffectiveDiscount();
                          const itemDiscount = effectiveDiscountTotal * itemRatio;
                          const discountPercent = discount.typ_rabatu === 'procentowy' ? parseFloat(discount.wartosc) || 0 : (total > 0 ? (itemDiscount / total) * 100 : 0);

                          return (
                            <span
                              key={discount.id}
                              style={{
                                backgroundColor: "var(--success-light)",
                                color: "var(--success)",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontWeight: "600",
                                fontSize: "9px",
                              }}
                            >
                              -{discountPercent.toFixed(0)}%
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>-</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontSize: "11px",
                      fontWeight: "600",
                    }}
                  >
                    {/* Kaucje i produkty wykluczone nie mają rabatu - nie pokazuj przekreślonej ceny */}
                    {appliedDiscounts.length > 0 && taxRate !== -2 && item.typ_produktu !== 'kaucja' && !item.wykluczony_z_rabatow && (
                      <div style={{ fontSize: "9px", color: "var(--success)", marginBottom: "2px" }}>
                        <span style={{ textDecoration: "line-through", color: "var(--text-muted)" }}>
                          {total.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {(() => {
                      // Kaucje (VAT = -2) i produkty wykluczone z rabatów nie mają rabatu
                      const isDeposit = taxRate === -2 || item.typ_produktu === 'kaucja';
                      const isExcluded = item.wykluczony_z_rabatow;
                      if (isDeposit || isExcluded) return total.toFixed(2);

                      // Oblicz finalną wartość po rabacie (tylko dla produktów które mogą mieć rabat)
                      const cartTotalForDiscount = cart.reduce((sum, i) => {
                        const vat = parseFloat(i.stawka_vat);
                        if (vat === -2 || i.typ_produktu === 'kaucja') return sum;
                        if (i.wykluczony_z_rabatow) return sum; // Wykluczone z rabatów
                        return sum + (parseFloat(i.price) || 0) * (parseFloat(i.quantity) || 0);
                      }, 0);
                      const itemRatio = cartTotalForDiscount > 0 ? total / cartTotalForDiscount : 0;
                      // Użyj efektywnego rabatu zamiast oryginalnego
                      const effectiveDiscountTotal = getEffectiveDiscount();
                      const itemDiscount = effectiveDiscountTotal * itemRatio;
                      const finalValue = total - itemDiscount;
                      return finalValue.toFixed(2);
                    })()} zł
                  </td>
                  <td
                    style={{ padding: "6px 4px", textAlign: "center" }}
                  >
                    {(item.current_purchase_price > 0 || item.cena_zakupu > 0 || item.fifo_purchase_price > 0) ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1px",
                        }}
                      >
                        {/* FIFO — nowa metoda (najstarsza partia) */}
                        {item.fifo_purchase_price > 0 ? (
                          <div title={`FIFO: zakup ${parseFloat(item.fifo_purchase_price).toFixed(2)} zł${discountCoeff < 1 ? ' • marża po rabacie' : ''}`}>
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: "600",
                                color:
                                  fifoMargaPercent >= 30
                                    ? "var(--success)"
                                    : fifoMargaPercent >= 15
                                      ? "var(--warning)"
                                      : "var(--danger)",
                                padding: "1px 4px",
                                borderRadius: "3px",
                                backgroundColor:
                                  fifoMargaPercent >= 30
                                    ? "var(--success-light)"
                                    : fifoMargaPercent >= 15
                                      ? "var(--warning-light)"
                                      : "var(--danger-light)",
                              }}
                            >
                              {fifoMargaPercent.toFixed(1)}%
                            </span>
                            <span style={{ fontSize: "8px", color: "var(--text-muted)", marginLeft: "2px" }}>FIFO</span>
                          </div>
                        ) : null}

                        {/* Stara metoda — ostatnia faktura */}
                        <div title={`Ost. faktura: zakup ${(item.current_purchase_price || item.cena_zakupu || 0).toFixed ? parseFloat(item.current_purchase_price || item.cena_zakupu || 0).toFixed(2) : '0.00'} zł`}>
                          <span
                            style={{
                              fontSize: item.fifo_purchase_price > 0 ? "9px" : "10px",
                              fontWeight: item.fifo_purchase_price > 0 ? "400" : "500",
                              color:
                                margaPercent >= 30
                                  ? "var(--success)"
                                  : margaPercent >= 15
                                    ? "var(--warning)"
                                    : "var(--danger)",
                              padding: "1px 4px",
                              borderRadius: "3px",
                              opacity: item.fifo_purchase_price > 0 ? 0.7 : 1,
                              backgroundColor: item.fifo_purchase_price > 0
                                ? "transparent"
                                : (margaPercent >= 30
                                    ? "var(--success-light)"
                                    : margaPercent >= 15
                                      ? "var(--warning-light)"
                                      : "var(--danger-light)"),
                            }}
                          >
                            {margaPercent.toFixed(1)}%
                          </span>
                          <span style={{ fontSize: "8px", color: "var(--text-muted)", marginLeft: "2px" }}>
                            {item.fifo_purchase_price > 0 ? 'ost.fakt.' : (item.purchase_price_method?.includes('faktury') ? 'Faktura' : 'Tabela')}
                          </span>
                        </div>

                        <span
                          style={{ fontSize: "8px", color: "var(--text-muted)" }}
                        >
                          {(item.fifo_margin_amount != null && item.fifo_purchase_price > 0)
                            ? parseFloat(item.fifo_margin_amount).toFixed(2)
                            : margaAbsolute.toFixed(2)} zł
                        </span>
                      </div>
                    ) : (
                      <span
                        style={{ fontSize: "10px", color: "var(--text-muted)" }}
                      >
                        -
                      </span>
                    )}
                  </td>
                  <td
                    style={{ padding: "8px 6px", textAlign: "center" }}
                  >
                    <div style={{ display: "flex", gap: "3px", justifyContent: "center" }}>
                      {/* Przycisk wykluczenia z rabatów - nie pokazuj dla kaucji */}
                      {!item.is_deposit && (
                        <button
                          onClick={() => toggleDiscountExclusion(item)}
                          style={{
                            background: item.wykluczony_z_rabatow ? "var(--danger)" : "var(--text-muted)",
                            color: "#fff",
                            width: "22px",
                            height: "22px",
                            fontSize: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0
                          }}
                          title={item.wykluczony_z_rabatow ? "Wykluczony z rabatu (ta sprzedaż) - kliknij aby przywrócić" : "Wyklucz z rabatu (tylko ta sprzedaż)"}
                        >
                          {item.wykluczony_z_rabatow ? "🚫" : "💰"}
                        </button>
                      )}
                      <button
                        onClick={() => removeFromCart(item.id)}
                        style={{
                          border: '1px solid var(--danger)', color: 'var(--danger)',
                          width: "32px",
                          height: "32px",
                          minWidth: "32px",
                          minHeight: "32px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0
                        }}
                        title="Usuń z koszyka"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              );
              });
            })()}
            {/* Wyszukiwarka jako ostatnia pozycja w koszyku */}
            <tr
              style={{
                backgroundColor: "var(--bg)",
                borderTop: "2px solid var(--border)",
              }}
            >
              <td colSpan="10" style={{ padding: "12px" }}>
                <ProductSearch
                  onProductSelect={handleProductSelect}
                  selectedProducts={cart}
                  searchQuery={productSearchQuery}
                  onSearchQueryChange={setProductSearchQuery}
                  selectedCategory={selectedCategory}
                  onlyAvailable={onlyAvailable}
                  showFilters={false}
                  locationId={currentLocationId}
                  premiumProducts={premiumProducts}
                />
              </td>
            </tr>
          </tbody>
          <tfoot
            style={{
              backgroundColor: "var(--bg)",
              borderTop: "2px solid var(--border)",
            }}
          >
            {/* ...stopka koszyka... */}
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default React.memo(CartTable);
